import { EmptyObject } from '@crossbeam/types';

import { chunk } from 'lodash';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import type { DeclineReasonType } from '@/constants/decline_reasons';
import { TEMP_PARTNER_TRACKER_ACCESS } from '@/constants/feature_flags';
import { captureException } from '@/errors';
import { ls } from '@/local_storage';
import { useFeatureFlagStore } from '@/stores/FeatureFlagStore';
import { initStore } from '@/stores/store-utils';
import {
  BulkTagItemType,
  OfflinePartner,
  OfflinePartnerTrimmed,
  OverlapCounts,
  Partner,
  PartnerTag,
  PartnerTagMap,
  PartnerTrimmed,
  PublicInviteOrg,
  UpdateVisibilityPayload,
  isManagedOfflinePartner,
  isOfflinePartner,
} from '@/types/partners';
import {
  OldSentPartnershipProposal,
  ReceivedPartnershipProposal,
  SentPartnershipProposal,
  isNewProposal,
} from '@/types/proposal';

import { useFlashesStore } from './FlashesStore';

const BULK_TAG_BATCH_SIZE = 100;

type BulkTagFailureType = { status: number | null; messages: string[] };

async function putTagBatch(
  batch: BulkTagItemType[],
): Promise<BulkTagFailureType | null> {
  try {
    const { error, response } = await crossbeamApi.PUT(
      '/v0.1/partners/tags/bulk',
      { body: { items: batch } },
    );
    if (response.ok) return null;
    const messages = (error as { errors?: string[] } | undefined)?.errors;
    return { status: response.status, messages: messages ?? [] };
  } catch (error) {
    captureException(error);
    return { status: null, messages: [] };
  }
}

export const usePartnersStore = defineStore('PartnersStore', () => {
  const partnerOrgs = ref<Partner[]>([]);
  const offlinePartners = ref<OfflinePartner[]>([]);
  const managedOfflinePartners = ref<Partner[]>([]);
  const partnerOrgsLookup = ref<Record<string, Partner | OfflinePartner>>({});
  const proposalsSent = ref<SentPartnershipProposal[]>([]);
  const hasLoadedSentProposals = ref(false);
  const proposalsReceived = ref<ReceivedPartnershipProposal[]>([]);
  const overlapCounts = ref<OverlapCounts | EmptyObject>({});
  const partnersByTag = ref<PartnerTagMap>({});
  const partnerTags = ref<PartnerTag[]>([]);
  const partnerFilters = ref<Record<string, boolean>>({});
  const publicInviteOrg = ref<PublicInviteOrg>(null);
  const publicInviteCode = ref<string>('');
  const hasRestrictedVisibility = ref(false);
  const { hasScope } = useAuth();

  const { addSuccessFlash, addErrorFlash } = useFlashesStore();

  async function fetchAllSentProposals(): Promise<{
    data: { items: OldSentPartnershipProposal[] } | null;
    error: unknown;
  }> {
    const PAGE_SIZE = 200;
    const items: OldSentPartnershipProposal[] = [];

    async function getPage(page: number) {
      return await crossbeamApi.GET('/v0.1/proposals', {
        params: { query: { limit: PAGE_SIZE, page } },
      });
    }

    const { data, error } = await getPage(1);
    if (error) return { data: null, error };
    items.push(...(data?.items ?? []));

    const lastPage = data?.pagination?.last_page ?? 1;
    for (let page = 2; page <= lastPage; page += 1) {
      const { data: pageData, error: pageError } = await getPage(page);
      if (pageError) return { data: null, error: pageError };
      items.push(...(pageData?.items ?? []));
    }

    return { data: { items }, error: null };
  }

  const { initError, ready, readySync, running, refresh } = initStore(
    async (opts = { useCache: false }) => {
      try {
        const publicInvite: { code: string; org: PublicInviteOrg } =
          ls.publicInvite.get();
        if (publicInvite) {
          setPublicInviteCode(publicInvite.code);
          setPublicInviteOrg(publicInvite.org);
        }

        const [
          { data: partnersResponse, error: partnersError },
          { data: partnerTagsResponse, error: partnerTagsError },
          { data: proposalsSentResponse, error: proposalsSentError },
          { data: proposalsReceivedResponse, error: proposalsReceivedError },
        ] = await Promise.all([
          crossbeamApi.GET('/v0.2/partners'),
          crossbeamApi.GET('/v0.1/partner-tags'),
          fetchAllSentProposals(),
          // openapi-fetch rejects on network errors, which would skip the partner state below
          crossbeamApi
            .GET('/v0.1/proposals-received')
            .catch((error) => ({ data: undefined, error })),
          // Only call this on store initialization, not on refreshes...
          opts.useCache
            ? refreshOverlapUsage().catch(captureException)
            : Promise.resolve(),
        ]);
        if (partnersError) throw partnersError;
        if (partnerTagsError) throw partnerTagsError;
        if (proposalsSentError) throw proposalsSentError;

        partnerTags.value = partnerTagsResponse?.items ?? [];
        partnerTagsResponse?.items.forEach((tag) => {
          if (!partnerFilters.value[String(tag.id)]) {
            partnerFilters.value[String(tag.id)] = false;
          }
        });

        // Set partners
        partnerOrgs.value =
          partnersResponse?.items.sort((a, b) =>
            a.name.localeCompare(b.name),
          ) ?? [];
        offlinePartners.value =
          partnersResponse?.items.filter(isOfflinePartner) ?? [];
        managedOfflinePartners.value =
          partnersResponse?.items.filter(isManagedOfflinePartner) ?? [];
        // Declined sent proposals are only surfaced for Partner Tracker orgs
        // (the Sent/All tabs show them with a decline reason). For everyone
        // else, preserve the pre-Partner-Tracker behavior of returning only
        // live (non-declined) sent proposals, so onboarding progress, invite
        // counts, and re-invite state are unaffected by the backend now
        // returning declined proposals to all orgs.
        const { hasFeatureFlag } = useFeatureFlagStore();
        const sentProposals =
          proposalsSentResponse?.items.map(formatOldStyleProposal) ?? [];
        proposalsSent.value = hasFeatureFlag(TEMP_PARTNER_TRACKER_ACCESS)
          ? sentProposals
          : sentProposals.filter((proposal) => proposal.status !== 'declined');
        hasLoadedSentProposals.value = true;
        partnerOrgsLookup.value = partnerOrgs.value.reduce<
          Record<number, Partner | OfflinePartner>
        >((orgMap, org) => {
          orgMap[org.id] = org;
          return orgMap;
        }, {});

        partnersByTag.value = partnerOrgs.value.reduce<
          Record<string, number[]>
        >((tagLookup, org) => {
          if (!org.tags) return tagLookup;
          org.tags.forEach((tag) => {
            const tagId = String(tag.id);
            if (!tagLookup[tagId]) tagLookup[tagId] = [];
            tagLookup[tagId].push(org.id);
          });
          return tagLookup;
        }, {});

        if (proposalsReceivedError) throw proposalsReceivedError;
        proposalsReceived.value = proposalsReceivedResponse?.items ?? [];
      } catch (err) {
        captureException(err);
      }
    },
  );

  refresh({ useCache: true });

  const pendingSentProposals = computed(() =>
    proposalsSent.value.filter((proposal) => proposal.status === 'pending'),
  );

  // Methods
  function getPartnerOrgById(id: number, trimmed = true) {
    const partnerOrg = partnerOrgsLookup.value[id];
    if (!partnerOrg) return;
    return trimmed ? trimOrg(partnerOrg) : partnerOrg;
  }

  const partnerUUIDLookup = computed(
    () => new Map(partnerOrgs.value.map((org) => [org.uuid, org])),
  );

  function getPartnerOrgByUuid(uuid: string, trimmed = true) {
    const partnerOrg = partnerUUIDLookup.value.get(uuid);
    if (!partnerOrg) return;
    return trimmed ? trimOrg(partnerOrg) : partnerOrg;
  }

  function getPartnersByTag(tagId: string) {
    return partnersByTag.value[tagId] || [];
  }

  function getPartnerTagById(id: string) {
    return partnerTags.value.find((tag) => tag.id === id);
  }

  function getProposalReceivedById(id: number) {
    return proposalsReceived.value.find((proposal) => proposal.id === id);
  }

  function getProposalSentById(id: number) {
    return proposalsSent.value.find((proposal) => proposal.id === id);
  }

  function addProposal(proposal: SentPartnershipProposal) {
    const proposalIds = proposalsSent.value.map((proposal) => proposal.id);
    if (!proposalIds.includes(proposal.id)) {
      proposalsSent.value.push(formatOldStyleProposal(proposal));
    }
  }

  async function refreshOverlapUsage() {
    const usageResponse = await crossbeamApi.GET(
      '/v0.2/partners/global/overlap-counts',
    );
    overlapCounts.value = usageResponse.data ?? {};
  }

  function setPublicInviteOrg(org: PublicInviteOrg) {
    publicInviteOrg.value = org;
  }

  function setPublicInviteCode(code: string) {
    publicInviteCode.value = code;
  }

  function hydratePublicInviteFromLocalStorage() {
    const publicInvite: { code: string; org: PublicInviteOrg } | null =
      ls.publicInvite.get();
    if (!publicInvite?.code || !publicInvite?.org) return;
    setPublicInviteCode(publicInvite.code);
    setPublicInviteOrg(publicInvite.org);
  }

  function updateFilters(tagId: string) {
    partnerFilters.value[tagId] = !partnerFilters.value[tagId];
  }

  function resetPartnerFilters() {
    Object.keys(partnerFilters.value).forEach((tag) => {
      partnerFilters.value[tag] = false;
    });
  }

  async function deleteTag(tagId: string) {
    const { error } = await crossbeamApi.DELETE('/v0.1/partner-tags/{tag_id}', {
      params: {
        path: { tag_id: tagId },
      },
    });
    if (error) {
      captureException(error);
      throw error;
    }
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete partnerFilters.value[tagId];
    refresh();
  }

  /**
   * Saves in batches of 100, each all-or-nothing, and stops at the first
   * failed batch. The store is refetched once if anything was written.
   *
   * @param items - Each item replaces one partner's whole tag set
   * @returns How many items were written, and why the failed batch failed
   */
  async function bulkEditTags(items: BulkTagItemType[]) {
    let savedCount = 0;
    let failure: BulkTagFailureType | null = null;
    for (const batch of chunk(items, BULK_TAG_BATCH_SIZE)) {
      failure = await putTagBatch(batch);
      if (failure) break;
      savedCount += batch.length;
    }
    if (savedCount) await refresh();
    return { savedCount, failure };
  }

  async function revokeProposal(proposalId: number) {
    const revokedCompany = proposalsSent.value.find(
      (proposal) => proposal.id === proposalId,
    )?.contact_company;
    try {
      const { error } = await crossbeamApi.PUT('/v0.1/proposals/{id}/revoke', {
        params: {
          path: {
            id: proposalId,
          },
        },
      });
      if (error) throw error;
    } catch (err) {
      captureException(err);
    } finally {
      // Invites are sent per contact, so a company can have several pending
      // proposals shown as a single grouped invite. The backend revokes them
      // all when one is revoked, so we optimistically drop every pending
      // proposal for that company to keep the list in sync before refresh.
      proposalsSent.value = proposalsSent.value.filter(
        (proposal) =>
          proposal.id !== proposalId &&
          !(
            proposal.status === 'pending' &&
            proposal.contact_company === revokedCompany
          ),
      );
      refresh();
    }
  }

  async function declineProposal(
    proposalId: number,
    payload?: {
      decline_reason?: DeclineReasonType;
      decline_note?: string | null;
    },
  ) {
    try {
      const { error } = await crossbeamApi.PUT('/v0.1/proposals/{id}/decline', {
        params: {
          path: {
            id: proposalId,
          },
        },
        body: payload ?? null,
      });
      if (error) throw error;
      proposalsReceived.value = proposalsReceived.value.filter(
        (proposal) => proposal.id !== proposalId,
      );
    } finally {
      refresh();
    }
  }

  async function refreshProposals() {
    if (!hasScope('write:partnerships')) return;
    try {
      const { data, error } = await crossbeamApi.GET(
        '/v0.1/proposals-received',
      );
      if (error) throw error;
      proposalsReceived.value = data?.items ?? [];
    } catch (err) {
      captureException(err);
    }
  }

  async function fetchVisibility(partnerUuid: string) {
    try {
      const { data, error } = await crossbeamApi.GET(
        '/v0.1/partners/{uuid}/team-access',
        {
          params: {
            path: {
              uuid: partnerUuid,
            },
          },
        },
      );
      if (error) throw error;
      hasRestrictedVisibility.value =
        data?.roles.some((role) => !role.has_access) ?? false;
      return data;
    } catch (error) {
      captureException(error);
      addErrorFlash({
        message: 'There was a problem loading your roles access.',
      });
    }
  }

  async function updateVisibility(
    partnerUuid: string,
    payload: UpdateVisibilityPayload,
    handleSuccess?: () => void,
  ) {
    try {
      const { data, error } = await crossbeamApi.PUT(
        '/v0.1/partners/{uuid}/team-access',
        {
          params: {
            path: {
              uuid: partnerUuid,
            },
          },
          body: payload,
        },
      );
      if (error) throw error;
      hasRestrictedVisibility.value =
        data?.roles.some((role) => !role.has_access) ?? false;
      addSuccessFlash({
        message: 'Your changes have been saved!',
      });
      handleSuccess?.();
    } catch (error) {
      captureException(error);
      addErrorFlash({
        message: 'There was a problem saving your changes.',
      });
    }
  }

  const hasPartners = computed(() => !!partnerOrgs.value.length);

  // A deprecated MOP whose Open Data Partner twin we already partner with has
  // nothing to convert to, so it is offered for deletion instead and left out
  // of the replace flow entirely.
  const replaceableManagedOfflinePartners = computed(() =>
    managedOfflinePartners.value.filter((partner) => {
      if (!partner.is_mop) return false;
      const openDataPartnerId = partner.odp_org_id;
      if (openDataPartnerId == null) return true;
      return !partnerOrgsLookup.value[openDataPartnerId];
    }),
  );

  return {
    error: initError,
    ready,
    readySync,
    running,
    refreshPartnersStore: refresh,
    partnerOrgs,
    offlinePartners,
    managedOfflinePartners,
    replaceableManagedOfflinePartners,
    partnerOrgsLookup,
    proposalsSent,
    hasLoadedSentProposals,
    pendingSentProposals,
    proposalsReceived,
    overlapCounts,
    partnersByTag,
    partnerTags,
    partnerFilters,
    publicInviteOrg,
    publicInviteCode,
    getPartnerOrgById,
    getPartnerOrgByUuid,
    getPartnersByTag,
    getPartnerTagById,
    getProposalReceivedById,
    getProposalSentById,
    hasPartners,
    hasRestrictedVisibility,
    fetchVisibility,
    updateVisibility,
    addProposal,
    setPublicInviteOrg,
    setPublicInviteCode,
    hydratePublicInviteFromLocalStorage,
    updateFilters,
    resetPartnerFilters,
    deleteTag,
    bulkEditTags,
    revokeProposal,
    declineProposal,
    refreshProposals,
    refreshOverlapUsage,
  };
});

// Converts old schema for partnership proposals to new data schema
export function formatOldStyleProposal(
  proposal: OldSentPartnershipProposal | SentPartnershipProposal,
): SentPartnershipProposal {
  if (isNewProposal(proposal)) {
    return proposal;
  }
  return {
    id: proposal.id,
    status: proposal.status,
    created_at: proposal.created_at,
    updated_at: proposal.updated_at,
    contact_name: proposal.contact.name,
    contact_email: proposal.contact.email,
    contact_company: proposal.contact.company,
    partner_candidate_id: proposal.partner_candidate_id ?? null,
    sending_user: proposal.sending_user,
    migrated_from_reveal: proposal.migrated_from_reveal,
    decline_reason: proposal.decline_reason,
    decline_note: proposal.decline_note,
    declining_user: proposal.declining_user,
  };
}

export const trimOrg = (org: PartnerTrimmed | OfflinePartnerTrimmed) => {
  if (!org) return undefined;

  const trimmedOrg = { ...org };
  delete trimmedOrg.users;
  delete trimmedOrg.tags;

  return trimmedOrg;
};
